import type { AdminTabId } from '~/utils/adminTabMeta'

/** Clé localStorage : admin peut ouvrir le client joueur sans redirection auto. */
export const ADMIN_PLAY_MODE_STORAGE_KEY = 'pokegrind-admin-play-mode'

export const ADMIN_SHELL_PATH_PREFIX = '/admin'

export const ADMIN_ONLY_LEGACY_PATHS = ['/debug', '/cosmetics-demo'] as const

export type AdminPillarId =
  | 'overview'
  | 'players'
  | 'moderation'
  | 'operations'
  | 'platform'
  | 'observability'

export const ADMIN_PILLAR_ORDER: AdminPillarId[] = [
  'overview',
  'players',
  'moderation',
  'operations',
  'platform',
  'observability',
]

export const ADMIN_PILLAR_META: Record<
  AdminPillarId,
  { title: string; subtitle: string; path: string; supportAllowed: boolean }
> = {
  overview: {
    title: 'Vue d’ensemble',
    subtitle: 'Métriques live, alertes et accès rapides',
    path: '/admin',
    supportAllowed: true,
  },
  players: {
    title: 'Joueurs',
    subtitle: 'Comptes, fiches et actions ciblées',
    path: '/admin/players',
    supportAllowed: true,
  },
  moderation: {
    title: 'Modération',
    subtitle: 'Chat, signalements et bannissements',
    path: '/admin/moderation',
    supportAllowed: true,
  },
  operations: {
    title: 'Opérations',
    subtitle: 'Maintenance, connexions et audit',
    path: '/admin/operations',
    supportAllowed: true,
  },
  platform: {
    title: 'Plateforme jeu',
    subtitle: 'Modes, endgame, secrets et réglages avancés',
    path: '/admin/platform',
    supportAllowed: false,
  },
  observability: {
    title: 'Observabilité',
    subtitle: 'Erreurs client et santé gameplay',
    path: '/admin/observability',
    supportAllowed: true,
  },
}

/**
 * Source unique des sous-onglets par pilier : routage (`resolveAdminRoute`,
 * `pillarToPath`) **et** navigation (`AdminSidebarNav`, `AdminOperationsView`).
 * La sidebar en gardait une copie en dur — un onglet ajouté ici restait alors
 * accessible par URL mais invisible au menu.
 */
export const PLATFORM_TABS: AdminTabId[] = ['modes', 'economy', 'tower', 'ascension', 'secrets']
export const OPERATIONS_TABS: AdminTabId[] = ['maintenance', 'security', 'audit']
export const MODERATION_TABS: AdminTabId[] = ['chat', 'bans']
export const OBSERVABILITY_TABS: AdminTabId[] = ['logs']

export function isAdminShellPath(path: string): boolean {
  const p = path.split('?')[0] ?? path
  if (p === ADMIN_SHELL_PATH_PREFIX || p.startsWith(`${ADMIN_SHELL_PATH_PREFIX}/`)) return true
  return (ADMIN_ONLY_LEGACY_PATHS as readonly string[]).includes(p)
}

export function isAdminDebugPath(path: string): boolean {
  return (path.split('?')[0] ?? path) === '/admin/debug'
}

export function isAdminCosmeticsDemoPath(path: string): boolean {
  return (path.split('?')[0] ?? path) === '/admin/cosmetics-demo'
}

export function isAdminPlayModeEnabled(): boolean {
  if (import.meta.server) return false
  try {
    return localStorage.getItem(ADMIN_PLAY_MODE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function setAdminPlayModeEnabled(enabled: boolean): void {
  if (import.meta.server) return
  try {
    if (enabled) localStorage.setItem(ADMIN_PLAY_MODE_STORAGE_KEY, '1')
    else localStorage.removeItem(ADMIN_PLAY_MODE_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function adminTabBelongsToPillar(tab: AdminTabId): AdminPillarId {
  if (tab === 'dashboard') return 'overview'
  if (MODERATION_TABS.includes(tab)) return 'moderation'
  if (OPERATIONS_TABS.includes(tab)) return 'operations'
  if (PLATFORM_TABS.includes(tab)) return 'platform'
  if (OBSERVABILITY_TABS.includes(tab)) return 'observability'
  return 'overview'
}

export function defaultTabForPillar(pillar: AdminPillarId): AdminTabId {
  switch (pillar) {
    case 'moderation':
      return 'chat'
    case 'operations':
      return 'maintenance'
    case 'platform':
      return 'modes'
    case 'observability':
      return 'logs'
    case 'players':
    case 'overview':
    default:
      return 'dashboard'
  }
}

export function pillarToPath(pillar: AdminPillarId, tab?: AdminTabId): string {
  switch (pillar) {
    case 'overview':
      return '/admin'
    case 'players':
      return '/admin/players'
    case 'moderation':
      return `/admin/moderation/${tab && MODERATION_TABS.includes(tab) ? tab : 'chat'}`
    case 'operations':
      return `/admin/operations/${tab && OPERATIONS_TABS.includes(tab) ? tab : 'maintenance'}`
    case 'platform':
      return `/admin/platform/${tab && PLATFORM_TABS.includes(tab) ? tab : 'modes'}`
    case 'observability':
      return `/admin/observability/${tab && OBSERVABILITY_TABS.includes(tab) ? tab : 'logs'}`
    default:
      return '/admin'
  }
}

export function adminTabToPath(tab: AdminTabId): string {
  const pillar = adminTabBelongsToPillar(tab)
  if (pillar === 'players') return '/admin/players'
  if (pillar === 'overview') return '/admin'
  return pillarToPath(pillar, tab)
}

export function resolveAdminRoute(path: string): {
  pillar: AdminPillarId
  tab: AdminTabId
} {
  const p = path.split('?')[0] ?? path
  if (p === '/admin' || p === '/admin/') {
    return { pillar: 'overview', tab: 'dashboard' }
  }
  if (p === '/admin/players') {
    return { pillar: 'players', tab: 'dashboard' }
  }

  const moderation = p.match(/^\/admin\/moderation\/([a-z]+)$/)
  if (moderation?.[1]) {
    const id = moderation[1] as AdminTabId
    if (MODERATION_TABS.includes(id)) {
      return { pillar: 'moderation', tab: id }
    }
  }
  if (p === '/admin/moderation') {
    return { pillar: 'moderation', tab: 'chat' }
  }

  const operations = p.match(/^\/admin\/operations\/([a-z]+)$/)
  if (operations?.[1]) {
    const id = operations[1] as AdminTabId
    if (OPERATIONS_TABS.includes(id)) {
      return { pillar: 'operations', tab: id }
    }
  }
  if (p === '/admin/operations') {
    return { pillar: 'operations', tab: 'maintenance' }
  }

  const platform = p.match(/^\/admin\/platform\/([a-z]+)$/)
  if (platform?.[1]) {
    const id = platform[1] as AdminTabId
    if (PLATFORM_TABS.includes(id)) {
      return { pillar: 'platform', tab: id }
    }
  }
  if (p === '/admin/platform') {
    return { pillar: 'platform', tab: 'modes' }
  }

  const obs = p.match(/^\/admin\/observability\/([a-z]+)$/)
  if (obs?.[1]) {
    const id = obs[1] as AdminTabId
    if (id === 'chat') {
      return { pillar: 'moderation', tab: 'chat' }
    }
    if (OBSERVABILITY_TABS.includes(id)) {
      return { pillar: 'observability', tab: id }
    }
  }
  if (p === '/admin/observability') {
    return { pillar: 'observability', tab: 'logs' }
  }

  return { pillar: 'overview', tab: 'dashboard' }
}

export function postAuthLandingPath(role: string | undefined): string {
  if (role === 'admin' && !isAdminPlayModeEnabled()) return '/admin'
  return '/'
}
